import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Loader2, LogOut, ShieldAlert, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import PricingSettings from "@/components/admin/PricingSettings";
import BookingsPanel from "@/components/admin/BookingsPanel";
import EnquiriesPanel from "@/components/admin/EnquiriesPanel";
import LeadsPanel from "@/components/admin/LeadsPanel";
import ReviewsPanel from "@/components/admin/ReviewsPanel";
import SmsPanel from "@/components/admin/SmsPanel";

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();

  // null = checking, object = authed, false = not authed
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [leads, setLeads] = useState(null);
  const [reviewsCount, setReviewsCount] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [loading, setLoading] = useState(false);
  const [exchanging, setExchanging] = useState(false);

  // Synchronous hash check during render to detect OAuth callback
  const hash = location.hash || window.location.hash;
  const hasSessionId = hash?.includes("session_id=");

  useEffect(() => {
    let cancelled = false;

    async function processSessionId() {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) return;
      const session_id = decodeURIComponent(m[1]);
      setExchanging(true);
      try {
        const { data } = await apiClient.post("/auth/session", { session_id });
        if (cancelled) return;
        window.history.replaceState({}, "", "/admin");
        setUser(data);
        if (!data.is_admin) {
          toast.error("Signed in, but this account is not authorised.");
        } else {
          toast.success(`Welcome back${data.name ? `, ${data.name.split(" ")[0]}` : ""}.`);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          toast.error("Sign-in failed. Please try again.");
          setUser(false);
        }
      } finally {
        if (!cancelled) setExchanging(false);
      }
    }

    async function checkExisting() {
      try {
        const { data } = await apiClient.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (e) {
        if (!cancelled) setUser(false);
      }
    }

    if (hasSessionId) {
      processSessionId();
    } else {
      checkExisting();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.is_admin && enquiries === null) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadEnquiries() {
    try {
      const { data } = await apiClient.get("/enquiries?limit=200");
      setEnquiries(data);
    } catch (e) {
      toast.error("Could not load enquiries.");
    }
  }
  async function loadBookings() {
    try {
      const { data } = await apiClient.get("/admin/bookings?limit=200");
      setBookings(data);
    } catch (e) {
      console.error(e);
    }
  }
  async function loadLeads() {
    try {
      const { data } = await apiClient.get("/admin/leads?limit=200");
      setLeads(data);
    } catch (e) {
      console.error(e);
    }
  }
  async function refresh() {
    setLoading(true);
    await Promise.all([loadEnquiries(), loadBookings(), loadLeads()]);
    setLoading(false);
  }

  function signIn() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  async function signOut() {
    try {
      await apiClient.post("/auth/logout");
    } catch (e) { /* ignore */ }
    setUser(false);
    setEnquiries(null);
    toast.success("Signed out.");
    navigate("/admin");
  }

  if (user === null || exchanging) {
    return (
      <CenterScreen>
        <Loader2 className="w-6 h-6 animate-spin text-[#E60000]" />
        <div className="mt-4 text-zinc-400 text-sm tracking-wider uppercase">
          {exchanging ? "Completing sign-in…" : "Checking session…"}
        </div>
      </CenterScreen>
    );
  }

  if (user === false) return <SignInScreen onSignIn={signIn} />;
  if (!user.is_admin) return <NotAuthorisedScreen email={user.email} onSignOut={signOut} />;

  const TABS = [
    { k: "bookings", l: `Bookings · ${bookings?.length ?? 0}` },
    { k: "enquiries", l: `Enquiries · ${enquiries?.length ?? 0}` },
    { k: "leads", l: `Custom Quotes · ${leads?.length ?? 0}` },
    { k: "reviews", l: `Reviews${reviewsCount != null ? ` · ${reviewsCount}` : ""}` },
    { k: "pricing", l: "Pricing" },
    { k: "sms", l: "SMS" },
  ];

  const headline = (() => {
    switch (tab) {
      case "bookings": return { count: bookings?.length ?? "—", label: "bookings" };
      case "enquiries": return { count: enquiries?.length ?? "—", label: "enquiries" };
      case "leads": return { count: leads?.length ?? "—", label: "custom quote leads" };
      case "reviews": return { count: reviewsCount ?? "—", label: "reviews" };
      case "pricing": return { count: "", label: "pricing settings" };
      case "sms": return { count: "", label: "SMS reminders" };
      default: return { count: "", label: "" };
    }
  })();

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={BRAND.logoUrl} alt="MHJ" className="h-10 w-10 object-contain" />
            <div className="leading-none">
              <div className="font-display font-extrabold text-base tracking-tight">MHJ PRESTIGE · ADMIN</div>
              <div className="text-[10px] tracking-[0.3em] text-zinc-400 uppercase mt-1">Operations dashboard</div>
            </div>
          </a>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end leading-none">
              <div className="text-sm font-semibold">{user.name || user.email}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">{user.email}</div>
            </div>
            {user.picture ? (
              <img src={user.picture} alt="" className="h-10 w-10 rounded-none border border-white/15 object-cover" />
            ) : null}
            <button
              data-testid="admin-signout-button"
              onClick={signOut}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-[#E60000] hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 md:px-12 py-10 lg:py-14">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-3">
              <span className="inline-block w-8 h-px bg-[#E60000] align-middle mr-3" />
              Dashboard · Live
            </div>
            <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight">
              {headline.count !== "" && <>{headline.count} </>}
              <span className="text-zinc-500">{headline.label}</span>
            </h1>
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-sm hover:border-[#E60000] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Refresh
          </button>
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/10 flex-wrap" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold transition-colors border-b-2 -mb-px ${
                tab === t.k ? "text-white border-[#E60000]" : "text-zinc-500 border-transparent hover:text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "bookings" && <BookingsPanel items={bookings} reload={loadBookings} />}
        {tab === "enquiries" && <EnquiriesPanel items={enquiries} />}
        {tab === "leads" && <LeadsPanel items={leads} />}
        {tab === "reviews" && <ReviewsPanel onCountChange={setReviewsCount} />}
        {tab === "pricing" && <PricingSettings />}
        {tab === "sms" && <SmsPanel />}
      </main>
    </div>
  );
}

function SignInScreen({ onSignIn }) {
  return (
    <CenterScreen>
      <div className="max-w-md w-full">
        <img src={BRAND.logoUrl} alt="MHJ Prestige Transport" className="h-20 w-20 object-contain mx-auto" />
        <div className="mt-6 text-[11px] tracking-[0.3em] uppercase text-[#E60000] font-bold text-center">
          Admin · Restricted
        </div>
        <h1 className="font-display font-extrabold text-3xl lg:text-4xl text-center mt-3">
          Sign in to continue
        </h1>
        <p className="text-zinc-400 text-center text-sm mt-4">Authorised Google accounts only.</p>
        <button data-testid="admin-signin-button" onClick={onSignIn} className="btn-primary w-full justify-center mt-8">
          Continue with Google
        </button>
        <div className="text-center mt-6">
          <a href="/" className="text-zinc-500 hover:text-white text-xs tracking-widest uppercase">
            ← Back to site
          </a>
        </div>
      </div>
    </CenterScreen>
  );
}

function NotAuthorisedScreen({ email, onSignOut }) {
  return (
    <CenterScreen>
      <div className="max-w-md w-full text-center" data-testid="admin-not-authorised">
        <ShieldAlert className="w-12 h-12 text-[#E60000] mx-auto" />
        <h1 className="font-display font-extrabold text-3xl lg:text-4xl mt-6">Not authorised</h1>
        <p className="text-zinc-400 mt-4 text-sm">
          The account <span className="text-white font-semibold">{email}</span> is not on the admin allow-list.
        </p>
        <button data-testid="admin-not-authorised-signout" onClick={onSignOut} className="btn-ghost mt-8">
          Sign out
        </button>
      </div>
    </CenterScreen>
  );
}

function CenterScreen({ children }) {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="flex flex-col items-center w-full">{children}</div>
    </div>
  );
}
